import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

/**
 * Simple Battery Table - Daily battery voltage readings
 * 
 * Shows daily battery voltage averages for all devices in the organization.
 */
const SimpleBatteryTable = ({ selectedOrgId, startDate, endDate }) => {
  const [batteryData, setBatteryData] = useState({});
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo,
    isEndpointLoading
  } = useSharedPerformanceCache();

  useEffect(() => {
    loadBatteryData();
  }, [selectedOrgId, startDate, endDate]);

  const loadBatteryData = async () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    loadAllPerformanceData(selectedOrgId, startDate, endDate);
  };
  
  // Update battery data whenever cache changes
  useEffect(() => {
    const devices = getDevices();
    const batteryMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('battery-daily', device.id);
      if (data) {
        batteryMap[device.id] = data;
      }
    });
    
    setBatteryData(batteryMap);
  }, [cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  // Get devices from shared cache
  const devices = getDevices();
  
  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };
  
  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];


  return (
    <WalteroCard sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device Battery Voltage - Daily Averages
        </WalteroTypography>
        {cacheInfo.isLoading && (
          <CircularProgress size={16} />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
Daily battery voltage readings in volts.
      </Typography>

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0' }}>
                  Device
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0' }}>
                  Meter
                </TableCell>
                {sortedDates.map(date => (
                  <TableCell 
                    key={date} 
                    align="center" 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#fff3e0',
                      minWidth: '80px',
                      fontSize: '12px'
                    }}
                  >
                    {date.split('-').slice(1).join('/')}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0' }}>
                  Overall Avg
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.sort((a, b) => {
                const serialA = String(a.serialnumber || '');
                const serialB = String(b.serialnumber || '');
                return serialA.localeCompare(serialB);
              }).map(device => {
                const deviceBattery = batteryData[device.id];
                const overallAvg = deviceBattery?.summary?.overallAverage || 0;
                
                return (
                  <TableRow key={device.id}>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                      {device.serialnumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: '10px', color: '#666' }}>
                      {device.externalmeterid || 'N/A'}
                    </TableCell>
                    {sortedDates.map(date => {
                      const dayData = deviceBattery?.dailyBattery?.find(day => day.date === date);
                      const voltage = dayData?.averageVoltage;
                      const isLoading = isEndpointLoading('battery-daily', device.id);
                      
                      return (
                        <TableCell 
                          key={date} 
                          align="center"
                          sx={{
                            backgroundColor: voltage ? '#e8f5e8' : '#fafafa',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: voltage ? '#2e7d32' : '#999'
                          }}
                        >
                          {isLoading ? (
                            <CircularProgress size={12} />
                          ) : voltage ? (
                            `${voltage}V`
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: '600', 
                        backgroundColor: '#fff8e1',
                        color: overallAvg > 0 ? '#f57700' : '#999'
                      }}
                    >
                      {isEndpointLoading('battery-daily', device.id) ? (
                        <CircularProgress size={16} />
                      ) : overallAvg > 0 ? (
                        `${overallAvg}V`
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff8e1', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(batteryData).length} with battery data, {sortedDates.length} days
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default SimpleBatteryTable;